import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "nameInput", "nameCheckButton", "nameStatus",
    "authSection", "loginInput", "authButton", "authCodeField", "authCodeInput",
    "authRegistrationFields", "authEmailInput", "authPhoneInput",
    "authFirstNameInput", "authLastNameInput", "authMessage",
    "verificationSection",
    "emailSection", "emailInput", "emailCodeField", "emailCodeInput", "emailSendButton", "emailVerifyButton", "emailResendButton", "emailStatus",
    "phoneSection", "phoneInput", "phoneCodeField", "phoneCodeInput", "phoneSendButton", "phoneVerifyButton", "phoneResendButton", "phoneStatus",
    "createButton", "message"
  ]

  static values = {
    checkNameUrl: String,
    createUrl: String,
    sendOtpUrl: String,
    verifyOtpUrl: String,
    registrationUrl: String,
    updateContactUrl: String,
    isAuthenticated: String,
    messages: Object
  }

  connect() {
    this.nameAvailable = false
    this.authState = "login"
    this.authChallengeToken = null
    this.authRegistrationToken = null
    this.emailChallengeToken = null
    this.phoneChallengeToken = null
    this.emailResendTimeout = null
    this.phoneResendTimeout = null
    this.altchaVerified = false
    this.altchaPayload = null // Cache the payload when verified

    // Setup Altcha widget listener if it exists
    this.setupAltchaListener()

    // Check if we have a validated name from previous session
    const validatedName = localStorage.getItem('validatedCustomerName')
    if (validatedName && this.hasNameInputTarget) {
      this.nameInputTarget.value = validatedName
      this.nameAvailable = true
      // Hide name section and auto-proceed
      const nameSection = this.nameInputTarget.closest('.mb-6') || this.nameInputTarget.closest('form')
      if (nameSection) nameSection.classList.add('hidden')
      // Try to create customer - backend will tell us what's needed
      setTimeout(() => this.tryCreateCustomer(), 100)
    } else {
      // Clear any stale data if we're starting fresh
      localStorage.removeItem('validatedCustomerName')
    }
  }

  setupAltchaListener() {
    const widget = document.querySelector('altcha-widget')
    if (!widget) {
      // No widget in non-production environments
      this.altchaVerified = true
      return
    }

    // Check if widget is already verified
    const checkIfVerified = () => {
      const state = widget.getAttribute('state')
      const payload = widget.value || widget.getAttribute('payload')
      if (state === 'verified' && payload) {
        this.altchaVerified = true
        this.altchaPayload = payload
        this.enableSubmitButton()
        return true
      }
      return false
    }

    // Check initial state
    if (checkIfVerified()) {
      return
    }

    // Disable submit button until verified
    if (this.hasNameCheckButtonTarget) {
      this.nameCheckButtonTarget.disabled = true
      this.nameCheckButtonTarget.classList.add('btn-disabled')
    }

    // Listen for state changes
    widget.addEventListener('statechange', (e) => {
      if (e.detail.state === 'verified') {
        // Payload is in e.detail.payload
        const payload = e.detail.payload || widget.value || widget.getAttribute('payload')
        this.altchaVerified = true
        this.altchaPayload = payload
        this.enableSubmitButton()
      } else if (e.detail.state === 'error') {
        this.showError(this.t("verification_failed"))
        this.altchaVerified = false
      }
    })

    // Poll for verification state (in case event doesn't fire)
    const pollInterval = setInterval(() => {
      if (checkIfVerified()) {
        clearInterval(pollInterval)
      }
    }, 500)

    // Stop polling after 30 seconds
    setTimeout(() => {
      clearInterval(pollInterval)
    }, 30000)
  }

  enableSubmitButton() {
    if (this.hasNameCheckButtonTarget) {
      this.nameCheckButtonTarget.disabled = false
      this.nameCheckButtonTarget.classList.remove('btn-disabled')
    }
  }

  // Step 1: Check name availability
  async checkName(event) {
    if (event) event.preventDefault()

    const name = this.nameInputTarget.value.trim()
    if (!name) {
      this.showError(this.t("name_required"))
      return
    }

    // Check if altcha verification is required and completed
    if (!this.altchaVerified) {
      this.showError(this.t("verification_pending"))
      return
    }

    const altchaPayload = this.getAltchaPayload()
    if (!altchaPayload) {
      this.showError(this.t("verification_failed"))
      return
    }

    this.setLoading(this.nameCheckButtonTarget, true)
    this.clearError()

    try {
      const response = await fetch(this.checkNameUrlValue, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify({ name, altcha: altchaPayload })
      })

      const data = await response.json()

      if (response.ok && data.available) {
        this.nameAvailable = true
        this.showNameStatus(this.t("name_available"), "success")

        // Store validated name in localStorage to survive page reloads
        localStorage.setItem('validatedCustomerName', name)

        // Always hide the name form after successful validation
        const nameSection = this.nameInputTarget.closest('.mb-4') || this.nameInputTarget.closest('form')
        if (nameSection) nameSection.classList.add('hidden')

        this.proceedToNextStep()
      } else {
        this.nameAvailable = false
        this.showNameStatus(data.error || this.t("name_taken"), "error")
      }
    } catch (error) {
      this.showError(this.t("unable_to_check_name"))
    } finally {
      this.setLoading(this.nameCheckButtonTarget, false)
    }
  }

  proceedToNextStep() {
    if (!this.nameAvailable) return

    if (this.isAuthenticatedValue === "true") {
      this.tryCreateCustomer()
    } else {
      this.showAuthSection()
    }
  }

  showAuthSection() {
    if (this.hasAuthSectionTarget) {
      // Form is already hidden in checkName, just show auth section
      this.authSectionTarget.classList.remove("hidden")
      if (this.hasLoginInputTarget) this.loginInputTarget.focus()
    }
  }

  // Step 2: Authentication flow
  async handleAuth(event) {
    event.preventDefault()

    if (this.authState === "login") return this.sendAuthCode()
    if (this.authState === "code") return this.verifyAuthCode()
    if (this.authState === "registration") return this.completeRegistration()
  }

  async sendAuthCode() {
    const login = this.loginInputTarget.value.trim()
    if (!login) {
      this.showAuthError(this.t("enter_login"))
      return
    }

    this.setLoading(this.authButtonTarget, true, this.t("sending"))
    this.clearAuthError()

    try {
      const response = await fetch(this.sendOtpUrlValue, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify({ login })
      })

      const data = await response.json()
      if (!response.ok) throw new Error(data.error || this.t("send_failed"))

      this.authChallengeToken = data.challenge_token
      this.authState = "code"

      // Hide login input container
      if (this.hasLoginInputTarget) {
        const loginControl = this.loginInputTarget.closest('.form-control')
        if (loginControl) loginControl.classList.add("hidden")
      }

      // Show code field
      if (this.hasAuthCodeFieldTarget) {
        this.authCodeFieldTarget.classList.remove("hidden")
        this.authCodeInputTarget.focus()
      }

      this.showAuthMessage(this.t(data.login_type === "email" ? "code_sent_email" : "code_sent_phone"), "success")
    } catch (error) {
      this.showAuthError(error.message)
    } finally {
      this.finishAuthStep()
    }
  }

  async verifyAuthCode() {
    if (!this.authChallengeToken) {
      this.showAuthError(this.t("request_new_code"))
      return
    }

    const code = this.authCodeInputTarget.value.trim()
    if (!code) {
      this.showAuthError(this.t("enter_code"))
      return
    }

    this.setLoading(this.authButtonTarget, true, this.t("verifying"))

    try {
      const response = await fetch(this.verifyOtpUrlValue, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify({ challenge_token: this.authChallengeToken, code })
      })

      const data = await response.json()
      if (!response.ok) throw new Error(data.error || this.t("invalid_code"))

      if (data.status === "signed_in") {
        this.isAuthenticatedValue = "true"
        this.hideAuthSection()
        this.tryCreateCustomer()
        return
      }

      if (data.status === "needs_registration") {
        this.authRegistrationToken = data.registration_token
        this.authState = "registration"

        // Hide code field
        if (this.hasAuthCodeFieldTarget) {
          this.authCodeFieldTarget.classList.add("hidden")
        }

        // Show registration fields
        if (this.hasAuthRegistrationFieldsTarget) {
          this.authRegistrationFieldsTarget.classList.remove("hidden")
        }

        if (data.login_type === "email" && this.hasAuthEmailInputTarget) {
          this.authEmailInputTarget.value = data.login
          this.authEmailInputTarget.disabled = true
        }

        if (data.login_type === "phone" && this.hasAuthPhoneInputTarget) {
          this.authPhoneInputTarget.value = data.login
          this.authPhoneInputTarget.disabled = true
        }

        this.showAuthMessage(this.t("code_verified_register"), "success")
        return
      }

      this.showAuthError(this.t("unexpected"))
    } catch (error) {
      this.showAuthError(error.message)
    } finally {
      this.finishAuthStep()
    }
  }

  async completeRegistration() {
    if (!this.authRegistrationToken) {
      this.showAuthError(this.t("verify_first"))
      return
    }

    this.setLoading(this.authButtonTarget, true, this.t("creating_account"))

    const payload = {
      registration_token: this.authRegistrationToken,
      user: {
        first_name: this.authFirstNameInputTarget.value,
        last_name: this.authLastNameInputTarget.value,
        email: this.hasAuthEmailInputTarget ? this.authEmailInputTarget.value : undefined,
        phone_number: this.hasAuthPhoneInputTarget ? this.authPhoneInputTarget.value : undefined
      }
    }

    try {
      const response = await fetch(this.registrationUrlValue, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify(payload)
      })

      const data = await response.json()
      if (!response.ok) throw new Error(data.error || this.t("account_failed"))

      if (data.status === "registered") {
        this.isAuthenticatedValue = "true"
        this.hideAuthSection()
        this.tryCreateCustomer()
        return
      }

      this.showAuthError(this.t("unexpected"))
    } catch (error) {
      this.showAuthError(error.message)
    } finally {
      this.finishAuthStep()
    }
  }

  // setLoading restores the label the button had before the request; the
  // auth button's label follows the step instead.
  finishAuthStep() {
    this.setLoading(this.authButtonTarget, false)
    const labels = { login: "send_code", code: "verify_code", registration: "sign_up" }
    this.setButtonText(this.authButtonTarget, this.t(labels[this.authState]))
  }

  hideAuthSection() {
    if (this.hasAuthSectionTarget) {
      this.authSectionTarget.classList.add("hidden")
    }
  }

  // Step 3: Try to create customer (will show what verification is needed)
  async tryCreateCustomer() {
    if (!this.nameAvailable) {
      this.showError(this.t("check_availability_first"))
      return
    }

    const name = this.nameInputTarget.value.trim()
    const altchaPayload = this.getAltchaPayload()

    this.clearError()

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify({ name, altcha: altchaPayload })
      })

      const data = await response.json()

      if (response.ok && data.permalink) {
        localStorage.removeItem('validatedCustomerName')
        this.showSuccess(this.t("created_successfully"))
        setTimeout(() => {
          window.location.href = data.redirect_url || data.redirect_path
        }, 1000)
      } else if (data.needs_verification) {
        this.showVerificationSection()

        // Show email verification first if needed
        if (data.needs_email_verification || data.needs_email || !data.current_email) {
          this.showEmailVerification(data.current_email)
        }
        // Only show phone verification if email is NOT needed (one at a time)
        else if (data.needs_phone_verification || data.needs_phone || !data.current_phone) {
          this.showPhoneVerification(data.current_phone)
        }

        this.showMessage(this.t("verify_contact_info"), "info")
      } else {
        // Clear localStorage when creation fails - verification might have expired
        localStorage.removeItem('validatedCustomerName')
        this.nameAvailable = false
        this.showError(data.error || this.t("unable_to_create"))
      }
    } catch (error) {
      // Clear localStorage on error
      localStorage.removeItem('validatedCustomerName')
      this.nameAvailable = false
      this.showError(this.t("unable_to_create"))
    }
  }

  showVerificationSection() {
    if (this.hasVerificationSectionTarget) {
      this.verificationSectionTarget.classList.remove("hidden")
    }
  }

  showEmailVerification(currentEmail) {
    if (this.hasEmailSectionTarget) {
      this.emailSectionTarget.classList.remove("hidden")
      if (this.hasEmailInputTarget) {
        if (currentEmail) {
          this.emailInputTarget.value = currentEmail
        }
        this.emailInputTarget.focus()
      }
    }
  }

  showPhoneVerification(currentPhone) {
    if (this.hasPhoneSectionTarget) {
      this.phoneSectionTarget.classList.remove("hidden")
      if (this.hasPhoneInputTarget) {
        if (currentPhone) {
          this.phoneInputTarget.value = currentPhone
        }
        this.phoneInputTarget.focus()
      }
    }
  }

  // Check what's needed and proceed accordingly
  async checkAndProceed() {
    // Try to create the customer - the backend will tell us what's needed
    await this.tryCreateCustomer()
  }

  // Email verification
  handleEmailSubmit(event) {
    event.preventDefault()
    if (this.emailChallengeToken) {
      this.verifyEmailCode()
    } else {
      this.sendEmailCode()
    }
  }

  async sendEmailCode() {
    const email = this.hasEmailInputTarget ? this.emailInputTarget.value.trim() : ""
    if (!email) {
      this.showEmailStatus(this.t("please_enter_email"), "error")
      return
    }

    this.setLoading(this.emailSendButtonTarget, true, this.t("sending"))
    this.clearEmailStatus()

    try {
      const response = await fetch(this.sendOtpUrlValue, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify({ login: email })
      })

      const data = await response.json()
      if (!response.ok) throw new Error(data.error || this.t("send_failed"))

      this.emailChallengeToken = data.challenge_token
      this.showEmailStatus(this.t("code_sent_email"), "success")

      // Hide email input and send button
      if (this.hasEmailInputTarget) {
        this.emailInputTarget.closest(".form-control").classList.add("hidden")
      }
      if (this.hasEmailSendButtonTarget) {
        this.emailSendButtonTarget.classList.add("hidden")
      }

      // Show code field and verify button
      if (this.hasEmailCodeFieldTarget) {
        this.emailCodeFieldTarget.classList.remove("hidden")
      }
      if (this.hasEmailVerifyButtonTarget) {
        this.emailVerifyButtonTarget.classList.remove("hidden")
      }
      if (this.hasEmailCodeInputTarget) {
        this.emailCodeInputTarget.focus()
      }

      // Enable resend after 20 seconds
      this.startEmailResendTimer()
    } catch (error) {
      this.showEmailStatus(error.message, "error")
    } finally {
      this.setLoading(this.emailSendButtonTarget, false)
    }
  }

  startEmailResendTimer() {
    if (this.hasEmailResendButtonTarget) {
      this.emailResendButtonTarget.disabled = true

      if (this.emailResendTimeout) {
        clearTimeout(this.emailResendTimeout)
      }

      this.emailResendTimeout = setTimeout(() => {
        if (this.hasEmailResendButtonTarget) {
          this.emailResendButtonTarget.classList.remove("hidden")
          this.emailResendButtonTarget.disabled = false
        }
      }, 20000)
    }
  }

  async resendEmailCode() {
    // Reset challenge and resend
    this.emailChallengeToken = null
    if (this.hasEmailResendButtonTarget) {
      this.emailResendButtonTarget.classList.add("hidden")
    }
    await this.sendEmailCode()
  }

  async verifyEmailCode() {
    if (!this.emailChallengeToken) {
      // This shouldn't happen with the new flow, but handle gracefully
      await this.sendEmailCode()
      return
    }

    const code = this.emailCodeInputTarget.value.trim()
    if (!code) {
      this.showEmailStatus(this.t("enter_code"), "error")
      return
    }

    this.setLoading(this.emailVerifyButtonTarget, true, this.t("verifying"))

    try {
      // First verify the code
      const verifyResponse = await fetch(this.verifyOtpUrlValue, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify({ challenge_token: this.emailChallengeToken, code })
      })

      const verifyData = await verifyResponse.json()
      if (!verifyResponse.ok) throw new Error(verifyData.error || this.t("invalid_code"))

      // Then update the contact
      const updateResponse = await fetch(this.updateContactUrlValue, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify({ challenge_token: this.emailChallengeToken })
      })

      const updateData = await updateResponse.json()

      // Handle account merge
      if (updateData.status === "merged") {
        this.showEmailStatus(updateData.message || this.t("accounts_merged"), "success")
        // Reload to refresh with merged account
        setTimeout(() => window.location.reload(), 1000)
        return
      }

      if (!updateResponse.ok) throw new Error(updateData.error || this.t("email_update_failed"))

      this.showEmailStatus(this.t("email_verified"), "success")
      this.emailChallengeToken = null

      // Hide email section completely
      if (this.hasEmailSectionTarget) {
        this.emailSectionTarget.classList.add("hidden")
      }

      // Check what else is needed before creating
      this.checkAndProceed()
    } catch (error) {
      this.showEmailStatus(error.message, "error")
      this.setButtonText(this.emailVerifyButtonTarget, this.hasEmailVerifyButtonTarget ? this.emailVerifyButtonTarget.dataset.originalText || this.t("verify_code") : this.t("verify_code"))
    } finally {
      this.setLoading(this.emailVerifyButtonTarget, false)
    }
  }

  // Phone verification
  handlePhoneSubmit(event) {
    event.preventDefault()
    if (this.phoneChallengeToken) {
      this.verifyPhoneCode()
    } else {
      this.sendPhoneCode()
    }
  }

  async sendPhoneCode() {
    const phone = this.hasPhoneInputTarget ? this.phoneInputTarget.value.trim() : ""
    if (!phone) {
      this.showPhoneStatus(this.t("please_enter_phone"), "error")
      return
    }

    this.setLoading(this.phoneSendButtonTarget, true, this.t("sending"))
    this.clearPhoneStatus()

    try {
      const response = await fetch(this.sendOtpUrlValue, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify({ login: phone })
      })

      const data = await response.json()
      if (!response.ok) throw new Error(data.error || this.t("send_failed"))

      this.phoneChallengeToken = data.challenge_token
      this.showPhoneStatus(this.t("code_sent_phone"), "success")

      // Hide phone input and send button
      if (this.hasPhoneInputTarget) {
        this.phoneInputTarget.closest(".form-control").classList.add("hidden")
      }
      if (this.hasPhoneSendButtonTarget) {
        this.phoneSendButtonTarget.classList.add("hidden")
      }

      // Show code field and verify button
      if (this.hasPhoneCodeFieldTarget) {
        this.phoneCodeFieldTarget.classList.remove("hidden")
      }
      if (this.hasPhoneVerifyButtonTarget) {
        this.phoneVerifyButtonTarget.classList.remove("hidden")
      }
      if (this.hasPhoneCodeInputTarget) {
        this.phoneCodeInputTarget.focus()
      }

      // Enable resend after 20 seconds
      this.startPhoneResendTimer()
    } catch (error) {
      this.showPhoneStatus(error.message, "error")
    } finally {
      this.setLoading(this.phoneSendButtonTarget, false)
    }
  }

  startPhoneResendTimer() {
    if (this.hasPhoneResendButtonTarget) {
      this.phoneResendButtonTarget.disabled = true

      if (this.phoneResendTimeout) {
        clearTimeout(this.phoneResendTimeout)
      }

      this.phoneResendTimeout = setTimeout(() => {
        if (this.hasPhoneResendButtonTarget) {
          this.phoneResendButtonTarget.classList.remove("hidden")
          this.phoneResendButtonTarget.disabled = false
        }
      }, 20000)
    }
  }

  async resendPhoneCode() {
    // Reset challenge and resend
    this.phoneChallengeToken = null
    if (this.hasPhoneResendButtonTarget) {
      this.phoneResendButtonTarget.classList.add("hidden")
    }
    await this.sendPhoneCode()
  }

  async verifyPhoneCode() {
    if (!this.phoneChallengeToken) {
      // This shouldn't happen with the new flow, but handle gracefully
      await this.sendPhoneCode()
      return
    }

    const code = this.phoneCodeInputTarget.value.trim()
    if (!code) {
      this.showPhoneStatus(this.t("enter_code"), "error")
      return
    }

    this.setLoading(this.phoneVerifyButtonTarget, true, this.t("verifying"))

    try {
      // First verify the code
      const verifyResponse = await fetch(this.verifyOtpUrlValue, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify({ challenge_token: this.phoneChallengeToken, code })
      })

      const verifyData = await verifyResponse.json()
      if (!verifyResponse.ok) throw new Error(verifyData.error || this.t("invalid_code"))

      // Then update the contact
      const updateResponse = await fetch(this.updateContactUrlValue, {
        method: "POST",
        headers: this.headers,
        body: JSON.stringify({ challenge_token: this.phoneChallengeToken })
      })

      const updateData = await updateResponse.json()

      // Handle account merge
      if (updateData.status === "merged") {
        this.showPhoneStatus(updateData.message || this.t("accounts_merged"), "success")
        // Reload to refresh with merged account
        setTimeout(() => window.location.reload(), 1000)
        return
      }

      if (!updateResponse.ok) throw new Error(updateData.error || this.t("phone_update_failed"))

      this.showPhoneStatus(this.t("phone_verified"), "success")
      this.phoneChallengeToken = null

      // Hide phone section completely
      if (this.hasPhoneSectionTarget) {
        this.phoneSectionTarget.classList.add("hidden")
      }

      // Check what else is needed before creating
      this.checkAndProceed()
    } catch (error) {
      this.showPhoneStatus(error.message, "error")
      this.setButtonText(this.phoneVerifyButtonTarget, this.hasPhoneVerifyButtonTarget ? this.phoneVerifyButtonTarget.dataset.originalText || this.t("verify_code") : this.t("verify_code"))
    } finally {
      this.setLoading(this.phoneVerifyButtonTarget, false)
    }
  }

  // Utility methods
  showNameStatus(text, tone) {
    if (!this.hasNameStatusTarget) return
    this.nameStatusTarget.textContent = text
    this.nameStatusTarget.classList.remove("text-error", "text-success")
    if (tone === "error") this.nameStatusTarget.classList.add("text-error")
    if (tone === "success") this.nameStatusTarget.classList.add("text-success")
  }

  showAuthMessage(text, tone = "info") {
    if (!this.hasAuthMessageTarget) return
    this.authMessageTarget.textContent = text
    this.authMessageTarget.classList.remove("text-error", "text-success")
    if (tone === "error") this.authMessageTarget.classList.add("text-error")
    if (tone === "success") this.authMessageTarget.classList.add("text-success")
  }

  showAuthError(text) {
    this.showAuthMessage(text, "error")
  }

  clearAuthError() {
    if (this.hasAuthMessageTarget) this.authMessageTarget.textContent = ""
  }

  showEmailStatus(text, tone) {
    if (!this.hasEmailStatusTarget) return
    this.emailStatusTarget.textContent = text
    this.emailStatusTarget.classList.remove("text-error", "text-success")
    if (tone === "error") this.emailStatusTarget.classList.add("text-error")
    if (tone === "success") this.emailStatusTarget.classList.add("text-success")
  }

  clearEmailStatus() {
    if (this.hasEmailStatusTarget) this.emailStatusTarget.textContent = ""
  }

  showPhoneStatus(text, tone) {
    if (!this.hasPhoneStatusTarget) return
    this.phoneStatusTarget.textContent = text
    this.phoneStatusTarget.classList.remove("text-error", "text-success")
    if (tone === "error") this.phoneStatusTarget.classList.add("text-error")
    if (tone === "success") this.phoneStatusTarget.classList.add("text-success")
  }

  clearPhoneStatus() {
    if (this.hasPhoneStatusTarget) this.phoneStatusTarget.textContent = ""
  }

  showMessage(text, tone = "info") {
    if (!this.hasMessageTarget) return
    this.messageTarget.textContent = text
    this.messageTarget.classList.remove("text-error", "text-success", "text-info")
    if (tone === "error") this.messageTarget.classList.add("text-error")
    if (tone === "success") this.messageTarget.classList.add("text-success")
    if (tone === "info") this.messageTarget.classList.add("text-info")
  }

  showError(text) {
    this.showMessage(text, "error")
  }

  showSuccess(text) {
    this.showMessage(text, "success")
  }

  clearError() {
    if (this.hasMessageTarget) this.messageTarget.textContent = ""
  }

  setLoading(button, isLoading, text = null) {
    if (!button) return
    if (isLoading && text && !button.dataset.originalText) {
      button.dataset.originalText = button.textContent
    }
    button.disabled = isLoading
    if (text) {
      this.setButtonText(button, text)
    } else if (!isLoading && button.dataset.originalText) {
      this.setButtonText(button, button.dataset.originalText)
      delete button.dataset.originalText
    }
  }

  setButtonText(button, text) {
    if (button) button.textContent = text
  }

  getAltchaPayload() {
    // Return cached payload if we have it
    if (this.altchaPayload) {
      return this.altchaPayload
    }

    const widget = document.querySelector('altcha-widget')
    if (!widget) return true // No widget means it's not production, allow submission

    // Widget exists - try to get payload
    const payload = widget.value || widget.getAttribute('payload')
    if (payload) {
      this.altchaPayload = payload
      return payload
    }

    console.warn('Altcha widget exists but no payload found. State:', widget.getAttribute('state'))
    return payload
  }

  // Messages come translated from the page (CustomersHelper#signup_form_messages).
  t(key) {
    return this.messagesValue[key] || key
  }

  get headers() {
    return {
      "Content-Type": "application/json",
      "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content
    }
  }
}
